/**
 * replay.ts — experience-replay buffer for Fitted Q-Iteration.
 *
 * FQI is BATCH: it collects transitions for a while, then refits Q on the
 * whole batch at once. The buffer stores (s, a, r, s', done) tuples in a
 * ring; old transitions fall off the back as new ones arrive.
 *
 * Storage layout — we keep FOUR flat typed-array pools:
 *   X      (B × N_FEATURES)         the action we took's features
 *   R      (B)                      the reward observed
 *   Xnext  (B × N_ACTIONS × N_FEATURES)   the FULL next-state candidate matrix
 *   Done   (B)                      1 if the episode ended on this transition
 *
 * We store the full next-state candidate matrix (not just the action taken)
 * because computing the bootstrap target r + γ·max_a' Q(s', a') needs Q for
 * ALL 32 candidates of s'. Storing them avoids recomputing features at
 * fit-time.
 *
 * Why a ring (not a sampled minibatch): linear FQI's closed-form solve is
 * exact on the whole buffer — there's no benefit to minibatching for a 12×12
 * system. The whole buffer is the dataset; older transitions age out.
 */

import { type VariantSpec, MOB_BASELINE } from './variant';

export interface ReplayOptions {
  capacity?: number;
  spec?: VariantSpec;
}

export class Replay {
  readonly capacity: number;
  readonly spec: VariantSpec;
  /** Per-transition stride in `X` (= spec.nFeatures). */
  readonly xStride: number;
  /** Per-transition stride in `Xnext` (= spec.nActions × spec.nFeatures). */
  readonly xnStride: number;
  readonly X: Float64Array;
  readonly R: Float64Array;
  readonly Xnext: Float64Array;
  readonly Done: Uint8Array;
  /** Number of valid transitions currently stored. */
  size = 0;
  /** Ring write cursor. */
  private cursor = 0;

  constructor(capacityOrOpts: number | ReplayOptions = 50_000,
              legacySpec?: VariantSpec) {
    // Backward-compat: accept (capacity) or (capacity, spec) positional args,
    // or a single opts object. Existing call sites `new Replay(50_000)` and
    // `new Replay(cap)` keep working with the baseline spec.
    let capacity: number;
    let spec: VariantSpec;
    if (typeof capacityOrOpts === 'number') {
      capacity = capacityOrOpts;
      spec = legacySpec ?? MOB_BASELINE;
    } else {
      capacity = capacityOrOpts.capacity ?? 50_000;
      spec = capacityOrOpts.spec ?? MOB_BASELINE;
    }
    this.capacity = capacity;
    this.spec = spec;
    this.xStride = spec.nFeatures;
    this.xnStride = spec.nActions * spec.nFeatures;
    this.X = new Float64Array(capacity * this.xStride);
    this.R = new Float64Array(capacity);
    this.Xnext = new Float64Array(capacity * this.xnStride);
    this.Done = new Uint8Array(capacity);
  }

  /**
   * Append one transition.
   *   `xTaken`    — nf values, the action we took's feature row
   *   `reward`    — scalar reward observed
   *   `xNextAll`  — nActions × nf values, the full candidate matrix of the
   *                 resulting state (or the terminal state's matrix)
   *   `done`      — true if the episode ended on this step
   */
  add(xTaken: Float64Array, reward: number, xNextAll: Float64Array, done: boolean): void {
    const i = this.cursor;
    this.X.set(xTaken.subarray(0, this.xStride), i * this.xStride);
    this.R[i] = reward;
    this.Xnext.set(xNextAll.subarray(0, this.xnStride), i * this.xnStride);
    this.Done[i] = done ? 1 : 0;
    this.cursor = (this.cursor + 1) % this.capacity;
    if (this.size < this.capacity) this.size++;
  }

  /** Reset to empty (called at the start of a fresh training run). */
  clear(): void {
    this.size = 0;
    this.cursor = 0;
  }
}
